/**
 * Typed API helpers for the sequence machine.
 *
 * Endpoints live on the same Cloud Run service as proposals/extract —
 * see app/webhooks/sequences.py for the contract.
 */

export type SequenceStatus = 'active' | 'paused' | 'archived'
export type EnrollmentStatus =
  | 'active'
  | 'paused'
  | 'replied'
  | 'bounced'
  | 'completed'
  | 'stopped'
export type Channel = 'email' | 'linkedin'

/** Which Workspace account a sequence sends as. See app/services/sequences/mailboxes.py. */
export type MailboxKey = 'rep' | 'onboarding' | 'relationships'

export interface MailboxOption {
  key: MailboxKey
  label: string
  /** Empty for 'rep' — that identity resolves per-sequence from the owner. */
  email: string
  display_name: string
  description: string
  /** False when the refresh token isn't set on this deploy; the handle can't send. */
  configured: boolean
}

export interface SequenceStep {
  step_number: number
  day_offset: number
  channel: Channel
  subject_template: string
  body_prompt: string
  fallback_subject: string
  fallback_body: string
}

export interface SequenceStats {
  enrolled_count: number
  sent_count: number
  replied_count: number
  bounced_count: number
}

export interface Sequence {
  id: string
  owner_email: string
  name: string
  description: string
  target_audience: string
  goal: string
  tone: string
  steps: SequenceStep[]
  status: SequenceStatus
  /** Sending account. Absent on sequences created before multi-mailbox; treat as 'rep'. */
  mailbox?: MailboxKey
  stats: SequenceStats
  created_at: string
  updated_at: string
}

export interface Contact {
  id: string
  owner_email: string
  first_name: string
  last_name: string
  full_name: string
  email: string
  /** Apollo email_status: "verified" (dispatch-safe), "extrapolated" /
   *  "guessed" / "unavailable" (high bounce risk; the enroll route
   *  skips these). "manual" when the rep typed the email in. */
  email_status:
    | 'verified'
    | 'extrapolated'
    | 'guessed'
    | 'unavailable'
    | 'manual'
    | ''
  personal_emails: string[]
  title: string
  company: string
  industry: string
  city: string
  state: string
  country: string
  linkedin_url: string
  apollo_id: string
  source: 'manual' | 'apollo' | 'csv'
  enrichment: Record<string, unknown>
  notes: string
  created_at: string
  updated_at: string
}

export interface SendLogEntry {
  step: number
  sent_at: string
  subject: string
  body: string
  gmail_message_id: string
  gmail_thread_id: string
  /** "skipped" = step deliberately not emailed (non-email `channel`). */
  status: 'sent' | 'failed' | 'skipped'
  error: string
  provider: string
  model: string
}

export interface Enrollment {
  id: string
  sequence_id: string
  contact_id: string
  owner_email: string
  status: EnrollmentStatus
  current_step: number
  enrolled_at: string
  next_send_at: string | null
  last_send_at: string | null
  reply_detected_at: string | null
  thread_id: string
  send_log: SendLogEntry[]
  /** Consecutive send failures; at 3 the enrollment is parked as `bounced`. */
  consecutive_failures?: number
  last_error?: string
  last_error_at?: string
  created_at: string
  updated_at: string
  contact?: {
    id: string
    full_name: string
    email: string
    title: string
    company: string
    /** Pipeline stage — flipped to "warm" by the reply detector. */
    lead_status?: 'cold' | 'warm' | 'engaged' | 'won' | 'lost'
  }
}

export type ProspectProvider = 'claude' | 'gemini'

export interface ProspectCandidate {
  apollo_id: string
  first_name: string
  /** Apollo's `/mixed_people/api_search` returns last names obfuscated
   *  (e.g. "Ma***i"). Real names land via `/people/bulk_match` at
   *  enrollment time. */
  last_name: string
  full_name: string
  /** Empty in search results — Apollo only reveals on enrichment. Use
   *  `has_email` to decide whether to show the affordance. */
  email: string
  /** Empty until enrichment runs; "verified" is the only value the
   *  enroll route accepts for Apollo-sourced contacts. */
  email_status: string
  has_email: boolean
  has_phone: boolean
  title: string
  company: string
  industry: string
  city: string
  state: string
  country: string
  linkedin_url: string
  source: 'apollo'
  enrichment: Record<string, unknown>
}

export interface ProspectResponse {
  filters: Record<string, unknown>
  filter_rationale: string
  provider: ProspectProvider
  model: string
  candidates: ProspectCandidate[]
  pagination: {
    page?: number
    per_page?: number
    total_entries?: number
    total_pages?: number
  }
}

export interface DraftResponse {
  steps: SequenceStep[]
  rationale: string
  provider: ProspectProvider
  model: string
}

function asJsonError(text: string, status: number): Error {
  try {
    const parsed = JSON.parse(text) as { detail?: string; error?: string }
    return new Error(parsed.detail ?? parsed.error ?? `HTTP ${status}`)
  } catch {
    return new Error(text || `HTTP ${status}`)
  }
}

async function jsonFetch<T>(
  apiBase: string,
  path: string,
  headers: HeadersInit,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
      ...(init.headers ?? {}),
    },
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  return (await res.json()) as T
}

// ── Prospecting ────────────────────────────────────────────────────────────

export async function prospectPeople(
  apiBase: string,
  headers: HeadersInit,
  prompt: string,
  opts: { page?: number; perPage?: number; provider?: ProspectProvider } = {},
): Promise<ProspectResponse> {
  return jsonFetch(apiBase, '/sequences/prospect', headers, {
    method: 'POST',
    body: JSON.stringify({
      prompt,
      page: opts.page ?? 1,
      per_page: opts.perPage ?? 25,
      ...(opts.provider ? { provider: opts.provider } : {}),
    }),
  })
}

// ── Drafter ────────────────────────────────────────────────────────────────

export async function draftSequence(
  apiBase: string,
  headers: HeadersInit,
  body: {
    name: string
    target_audience: string
    goal: string
    tone?: string
    step_count?: number
    days_span?: number
    provider?: ProspectProvider
  },
): Promise<DraftResponse> {
  return jsonFetch(apiBase, '/sequences/draft', headers, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

// ── Sequences CRUD ─────────────────────────────────────────────────────────

export async function listMailboxes(
  apiBase: string,
  headers: HeadersInit,
): Promise<MailboxOption[]> {
  const body = await jsonFetch<{ mailboxes: MailboxOption[] }>(
    apiBase,
    '/sequences/mailboxes',
    headers,
  )
  return body.mailboxes || []
}

export async function listSequences(
  apiBase: string,
  headers: HeadersInit,
  opts: { mine?: boolean } = {},
): Promise<Sequence[]> {
  const q = opts.mine ? '?mine=true' : ''
  const body = await jsonFetch<{ sequences: Sequence[] }>(
    apiBase,
    `/sequences${q}`,
    headers,
  )
  return body.sequences
}

export async function createSequence(
  apiBase: string,
  headers: HeadersInit,
  body: {
    name: string
    description?: string
    target_audience?: string
    goal?: string
    tone?: string
    mailbox?: MailboxKey
    steps: SequenceStep[]
  },
): Promise<Sequence> {
  return jsonFetch(apiBase, '/sequences', headers, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function getSequence(
  apiBase: string,
  headers: HeadersInit,
  id: string,
): Promise<Sequence> {
  return jsonFetch(apiBase, `/sequences/${encodeURIComponent(id)}`, headers)
}

export async function patchSequence(
  apiBase: string,
  headers: HeadersInit,
  id: string,
  patch: {
    name?: string
    description?: string
    target_audience?: string
    goal?: string
    tone?: string
    status?: SequenceStatus
    steps?: SequenceStep[]
  },
): Promise<Sequence> {
  return jsonFetch(apiBase, `/sequences/${encodeURIComponent(id)}`, headers, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export async function enrollContacts(
  apiBase: string,
  headers: HeadersInit,
  sequenceId: string,
  body: {
    contact_ids?: string[]
    contacts?: Array<Partial<ProspectCandidate>>
    first_send_at?: string
  },
): Promise<{
  enrolled: Enrollment[]
  enrolled_count: number
  skipped_count: number
  skipped_no_email_count: number
  skipped_unverified_count: number
  skipped_no_email_names: string[]
  skipped_unverified_names: string[]
}> {
  return jsonFetch(
    apiBase,
    `/sequences/${encodeURIComponent(sequenceId)}/enroll`,
    headers,
    { method: 'POST', body: JSON.stringify(body) },
  )
}

export async function listSequenceEnrollments(
  apiBase: string,
  headers: HeadersInit,
  sequenceId: string,
  status?: EnrollmentStatus,
): Promise<Enrollment[]> {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  const body = await jsonFetch<{ enrollments: Enrollment[] }>(
    apiBase,
    `/sequences/${encodeURIComponent(sequenceId)}/enrollments${q}`,
    headers,
  )
  return body.enrollments
}

export async function patchEnrollment(
  apiBase: string,
  headers: HeadersInit,
  id: string,
  patch: { status?: EnrollmentStatus; next_send_at?: string },
): Promise<Enrollment> {
  return jsonFetch(apiBase, `/enrollments/${encodeURIComponent(id)}`, headers, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

// ── Contacts ───────────────────────────────────────────────────────────────

export async function listContacts(
  apiBase: string,
  headers: HeadersInit,
): Promise<Contact[]> {
  const body = await jsonFetch<{ contacts: Contact[] }>(
    apiBase,
    '/contacts',
    headers,
  )
  return body.contacts
}

export async function bulkImportContacts(
  apiBase: string,
  headers: HeadersInit,
  contacts: Array<Partial<ProspectCandidate>>,
): Promise<{ contacts: Contact[]; count: number }> {
  return jsonFetch(apiBase, '/contacts/import', headers, {
    method: 'POST',
    body: JSON.stringify({ contacts }),
  })
}

// ── Deep prospect research ─────────────────────────────────────────────────

/** Result of POST /contacts/{id}/research. The full briefing markdown
 *  lands on `contact.enrichment.briefing` too, but the response also
 *  carries it directly so the UI can show it without a reload. */
export interface ResearchResult {
  briefing: string
  sources_attempted: string[]
  sources_loaded: string[]
  provider: string
  model: string
  grounded: boolean
  generated_at: string
}

export async function researchContact(
  apiBase: string,
  headers: HeadersInit,
  contactId: string,
): Promise<{ contact: Contact; research: ResearchResult }> {
  return jsonFetch(
    apiBase,
    `/contacts/${encodeURIComponent(contactId)}/research`,
    headers,
    { method: 'POST', body: JSON.stringify({}) },
  )
}

// ── External source adapters (Granola + Parkify Notetaker) ─────────────────

export interface SourceStatus {
  name: string
  label: string
  configured: boolean
}

export interface SourceNoteSummary {
  id: string
  title: string
  occurred_at: string
  duration_minutes: number
  snippet: string
  participants: string[]
}

export interface SourceNoteContent {
  id: string
  title: string
  occurred_at: string
  transcript: string
  summary: string
  action_items: string[]
  participants: string[]
  source_url: string
}

export async function listSources(
  apiBase: string,
  headers: HeadersInit,
): Promise<SourceStatus[]> {
  const body = await jsonFetch<{ sources: SourceStatus[] }>(
    apiBase,
    '/sources',
    headers,
  )
  return body.sources
}

export async function listSourceNotes(
  apiBase: string,
  headers: HeadersInit,
  sourceName: string,
): Promise<{ name: string; configured: boolean; notes: SourceNoteSummary[] }> {
  return jsonFetch(
    apiBase,
    `/sources/${encodeURIComponent(sourceName)}/notes`,
    headers,
  )
}

export async function fetchSourceNote(
  apiBase: string,
  headers: HeadersInit,
  sourceName: string,
  noteId: string,
): Promise<SourceNoteContent> {
  return jsonFetch(
    apiBase,
    `/sources/${encodeURIComponent(sourceName)}/notes/${encodeURIComponent(noteId)}`,
    headers,
  )
}

// ── Dashboard / metrics ────────────────────────────────────────────────────

/** A single metric card on the Sales Portal landing page. The backend's
 *  metric registry (app/services/sequences/metrics.py) is the source of
 *  truth — adding a metric there auto-extends the dashboard. */
export interface MetricCard {
  name: string
  label: string
  /** Logical group ("Proposals", "Outreach", …). Frontend renders one
   *  section per group. */
  group: string
  /** Already-formatted value (e.g. "12", "3.4%", "—"). */
  value: string
  raw_value: number
  sublabel: string
  trend: string
}

export async function getDashboardMetrics(
  apiBase: string,
  headers: HeadersInit,
  scope: 'mine' | 'all' = 'mine',
): Promise<{ scope: string; cards: MetricCard[] }> {
  return jsonFetch(
    apiBase,
    `/dashboard/metrics?scope=${encodeURIComponent(scope)}`,
    headers,
  )
}
